import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Star,
  Stethoscope,
  Video,
  Languages,
  BadgeCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import MockRazorpayModal from "../components/MockRazorpayModal";
import DoctorReviewsSection from "../components/DoctorReviewsSection";
import { api, formatApiErrorDetail } from "../lib/api";

const FALLBACK_DOCTOR_IMG =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=400&q=80";

function formatDateLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

export default function PatientDoctorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("video"); // 'opd' | 'video'
  const [activeDate, setActiveDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paying, setPaying] = useState(false);
  const [bookingCtx, setBookingCtx] = useState(null); // { appointment_id, ... }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/doctors/${id}`);
      setData(data);
      if (!activeDate) setActiveDate(data.dates[0]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doctor = data?.doctor;
  const fee = useMemo(() => {
    if (!doctor) return 0;
    return mode === "video"
      ? doctor.video_fee ?? doctor.consultation_fee
      : doctor.opd_fee ?? doctor.consultation_fee;
  }, [doctor, mode]);

  const slotsForDate = (data?.slots_by_date?.[activeDate] || []).filter((s) =>
    s.mode_allowed.includes(mode),
  );

  const proceedToPay = async () => {
    if (!selectedSlot) {
      toast.error("Please select a time slot");
      return;
    }
    setPaying(true);
    try {
      const { data: book } = await api.post("/appointments/book", {
        doctor_id: id,
        slot_id: selectedSlot.id,
        mode,
      });
      setBookingCtx(book);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPaying(false);
    }
  };

  const onPaymentSuccess = async (paymentId, signature) => {
    try {
      const { data: appt } = await api.post("/appointments/confirm-payment", {
        appointment_id: bookingCtx.appointment_id,
        razorpay_payment_id: paymentId,
        razorpay_signature: signature || "mock_signature",
      });
      setBookingCtx(null);
      toast.success("Appointment confirmed!");
      navigate(`/patient/appointments/${appt.id}?just_booked=1`, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Real Razorpay Checkout.js launcher
  const launchRealCheckout = async (ctx) => {
    const loadScript = () => new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });
    const ok = await loadScript();
    if (!ok) { toast.error("Failed to load Razorpay Checkout"); return; }
    const rzp = new window.Razorpay({
      key: ctx.key_id,
      amount: ctx.amount * 100,
      currency: ctx.currency || "INR",
      order_id: ctx.razorpay_order_id,
      name: "Healthink",
      description: `Consultation with ${ctx.doctor?.name}`,
      handler: (resp) => onPaymentSuccess(resp.razorpay_payment_id, resp.razorpay_signature),
      modal: { ondismiss: () => onPaymentFailure() },
      prefill: {},
      theme: { color: "#0EA5A4" },
    });
    rzp.open();
  };

  useEffect(() => {
    if (bookingCtx && !bookingCtx.mock_mode && bookingCtx.key_id && bookingCtx.key_id !== "rzp_test_MOCK") {
      launchRealCheckout(bookingCtx);
      // close the mock modal immediately if it was about to open
      setBookingCtx((c) => c && { ...c, _launched: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx?.appointment_id]);

  const onPaymentFailure = async () => {
    try {
      await api.post(`/appointments/${bookingCtx.appointment_id}/payment-failed`);
    } catch (_e) {
      /* ignore */
    }
    toast.error("Payment failed. Please try another slot or retry.");
    setBookingCtx(null);
    // Reload slots to re-show the freed one
    load();
    setSelectedSlot(null);
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="doctor-profile-page"
        className="flex-1 px-6 lg:px-10 py-6 lg:py-8 max-w-5xl w-full mx-auto space-y-6"
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="doctor-profile-back-btn"
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Back to doctors
        </button>

        {loading || !doctor ? (
          <div className="space-y-4">
            <div className="h-44 bg-muted rounded-3xl animate-pulse" />
            <div className="h-64 bg-muted rounded-3xl animate-pulse" />
          </div>
        ) : (
          <>
            {/* Header */}
            <section className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-6">
                <img
                  src={doctor.image_url || FALLBACK_DOCTOR_IMG}
                  alt={doctor.name}
                  className="h-32 w-32 rounded-2xl object-cover shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wide">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    {doctor.category}
                  </div>
                  <h1
                    data-testid="doctor-profile-name"
                    className="text-2xl sm:text-3xl font-bold tracking-tight mt-2"
                  >
                    {doctor.name}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-0.5">{doctor.qualification}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-amber-600">
                      <Star className="h-4 w-4 fill-current" />
                      <span className="font-semibold">{doctor.rating?.toFixed?.(1)}</span>
                      <span className="text-muted-foreground">({doctor.total_reviews} reviews)</span>
                    </span>
                    <span className="text-muted-foreground">
                      {doctor.experience_years} yrs experience
                    </span>
                    {doctor.languages?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Languages className="h-4 w-4" /> {doctor.languages.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {doctor.about && (
                <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
                  {doctor.about}
                </p>
              )}

              <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-border">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Consult</div>
                  <div className="text-lg font-semibold mt-0.5">₹{doctor.consultation_fee}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Video</div>
                  <div className="text-lg font-semibold mt-0.5">₹{doctor.video_fee}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">OPD</div>
                  <div className="text-lg font-semibold mt-0.5">₹{doctor.opd_fee}</div>
                </div>
              </div>
            </section>

            {/* Booking */}
            <section
              data-testid="booking-section"
              className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm"
            >
              <h2 className="text-xl font-semibold tracking-tight">Book an appointment</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Pick a mode and slot that works for you.
              </p>

              {/* Mode toggle */}
              <Tabs
                value={mode}
                onValueChange={(v) => {
                  setMode(v);
                  setSelectedSlot(null);
                }}
                className="mt-5"
              >
                <TabsList className="bg-secondary p-1 rounded-full h-12">
                  <TabsTrigger
                    value="video"
                    data-testid="booking-mode-video"
                    className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    <Video className="h-4 w-4 mr-1.5" /> Video
                  </TabsTrigger>
                  <TabsTrigger
                    value="opd"
                    data-testid="booking-mode-opd"
                    className="rounded-full px-5 h-10 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  >
                    <Stethoscope className="h-4 w-4 mr-1.5" /> OPD
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Date carousel */}
              <div className="mt-6 -mx-2 px-2 overflow-x-auto" data-testid="booking-date-carousel">
                <div className="flex gap-2 pb-2">
                  {data.dates.map((d) => {
                    const isActive = d === activeDate;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          setActiveDate(d);
                          setSelectedSlot(null);
                        }}
                        data-testid={`booking-date-${d}`}
                        className={
                          "shrink-0 px-4 py-3 rounded-2xl border transition-all min-w-[96px] " +
                          (isActive
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-white hover:border-primary/40")
                        }
                      >
                        <div className="text-xs font-medium uppercase tracking-wide opacity-80">
                          {formatDateLabel(d)}
                        </div>
                        <div className="text-base font-semibold mt-0.5">
                          {new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                          })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Slot grid */}
              <div className="mt-6">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  Available time slots ({mode})
                </div>
                {slotsForDate.length === 0 ? (
                  <div className="text-sm text-muted-foreground bg-secondary/60 rounded-xl p-4">
                    No {mode} slots on this day. Try another date or mode.
                  </div>
                ) : (
                  <div
                    data-testid="booking-slot-grid"
                    className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
                  >
                    {slotsForDate.map((s) => {
                      const disabled = s.is_booked;
                      const selected = selectedSlot?.id === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => setSelectedSlot(s)}
                          data-testid={`slot-${s.id}`}
                          className={
                            "h-12 rounded-xl text-sm font-medium border transition-all " +
                            (disabled
                              ? "border-border bg-muted text-muted-foreground line-through cursor-not-allowed"
                              : selected
                                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                                : "border-border bg-white hover:border-primary/40 hover:bg-secondary")
                          }
                        >
                          {s.start_time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Proceed */}
              <div className="mt-7 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm">
                  {selectedSlot ? (
                    <>
                      <span className="text-muted-foreground">Selected:</span>{" "}
                      <span className="font-medium">
                        {formatDateLabel(activeDate)}, {selectedSlot.start_time}
                      </span>
                      <span className="text-muted-foreground"> · {mode}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">Pick a slot to continue</span>
                  )}
                </div>
                <Button
                  data-testid="proceed-to-pay-btn"
                  size="lg"
                  disabled={!selectedSlot || paying}
                  onClick={proceedToPay}
                  className="h-12 rounded-full px-6 bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                >
                  {paying ? "Loading…" : `Proceed to Pay ₹${fee}`}
                </Button>
              </div>
            </section>

            <DoctorReviewsSection
              doctorId={id}
              rating={doctor?.rating}
              totalReviews={doctor?.total_reviews}
            />
          </>
        )}
      </main>

      {bookingCtx && !bookingCtx._launched && (
        <MockRazorpayModal
          open={!!bookingCtx}
          orderId={bookingCtx.razorpay_order_id}
          amount={bookingCtx.amount}
          currency={bookingCtx.currency}
          keyId={bookingCtx.key_id}
          doctor={bookingCtx.doctor}
          onSuccess={onPaymentSuccess}
          onFailure={onPaymentFailure}
        />
      )}
    </div>
  );
}
